import { CircularProgress, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/auth';
import {
  LoginBackground,
  LoginCard,
  LoginFooter,
} from '@/components/LoginCard';
import { routes } from '@/config/routes';
import type { AuthErrorKey } from '@/types/auth';

interface LoginLocationState {
  from?: string;
  reason?: AuthErrorKey;
}

const authErrorFallbackMap: Record<AuthErrorKey, string> = {
  authConfigurationError: 'auth.authConfigurationError',
  authInteractionInProgress: 'auth.authInteractionInProgress',
  authLoginCancelled: 'auth.authLoginCancelled',
  authNetworkError: 'auth.authNetworkError',
  authSessionExpired: 'auth.authSessionExpired',
  authUnauthorized: 'auth.authUnauthorized',
  authFailed: 'auth.authFailed',
};

export function LoginPage() {
  const { t } = useTranslation('entity-portal');
  const {
    status,
    login,
    selectAccount,
    availableAccounts,
    requiresAccountSelection,
    isAuthActionPending,
    isProfileLoading,
    errorKey,
    clearError,
  } = useAuth();
  const location = useLocation();
  const locationState = (location.state ?? {}) as LoginLocationState;

  const redirectPath = locationState.from ?? routes.home;

  const effectiveErrorKey = useMemo<AuthErrorKey | null>(() => {
    if (errorKey) {
      return errorKey;
    }

    return locationState.reason ?? null;
  }, [errorKey, locationState.reason]);

  const errorMessage = effectiveErrorKey
    ? t(authErrorFallbackMap[effectiveErrorKey])
    : undefined;

  const handleLogin = () => {
    clearError();
    void login(redirectPath);
  };

  const handleSelectAccount = (accountId: string) => {
    clearError();
    void selectAccount(accountId);
  };

  if (status === 'authenticated') {
    return <Navigate to={redirectPath} replace />;
  }

  if (status === 'loading') {
    return (
      <LoginBackground>
        <Stack spacing={2} alignItems="center" role="status" aria-live="polite">
          <CircularProgress aria-hidden />
          <Typography variant="body2" color="text.secondary">
            {t('auth.authSessionValidationLoadingLabel')}
          </Typography>
        </Stack>
      </LoginBackground>
    );
  }

  return (
    <LoginBackground footer={<LoginFooter />}>
      <LoginCard
        availableAccounts={availableAccounts}
        requiresAccountSelection={requiresAccountSelection}
        isLoading={isAuthActionPending}
        isSelectingAccount={isProfileLoading}
        errorMessage={errorMessage}
        onLogin={handleLogin}
        onSelectAccount={handleSelectAccount}
      />
    </LoginBackground>
  );
}
